import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { TOUR_INTERESTS, TOUR_PLANS } from "@/lib/content";
import { CODES } from "@/components/booking/Steps";

const INIT = {
  name: "", email: "", phone_code: "+44", phone: "", tour_type: "custom", date: "", pickup_location: "", pickup_time: "",
  dropoff_time: "", guests: 2, child_seat_count: 0, interests: [], message: "", website: "",
};

const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Please enter your name.";
  if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Please enter a valid email.";
  if (!/^[\d\s\-()]{5,20}$/.test(f.phone)) e.phone = "Please enter a valid phone number.";
  if (f.pickup_location.trim().length < 2) e.pickup_location = "Please enter your pickup location.";
  if (!f.pickup_time) e.pickup_time = "Please choose a pickup time.";
  if (!f.guests || f.guests < 1) e.guests = "At least 1 guest.";
  return e;
};

export const Field = ({ label, error, children, id }) => (
  <div>
    <label className="field-label" htmlFor={id}>{label}</label>
    {children}
    {error && <p className="field-error" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

const TourTypeToggle = ({ value, onChange }) => (
  <div className="grid gap-2 sm:grid-cols-3">
    {TOUR_PLANS.map(({ id: v, title: l }) => (
      <button
        type="button"
        key={v}
        onClick={() => onChange(v)}
        data-testid={`tour-type-${v}`}
        className={`h-12 rounded-[14px] border text-sm transition-colors ${value === v ? "border-gold bg-gold-light text-ink" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
      >
        {l}
      </button>
    ))}
  </div>
);

export const TourInquiryForm = ({ tourType }) => {
  const [f, setF] = useState(INIT);
  useEffect(() => {
    if (tourType) setF((x) => ({ ...x, tour_type: tourType }));
  }, [tourType]);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const toggleInterest = (i) =>
    setF({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      const count = Number(f.child_seat_count);
      await api.post("/tour-inquiries", { ...f, guests: Number(f.guests), child_seats: count > 0, child_seat_count: count });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (done)
    return (
      <div className="card-soft border border-gold-border bg-gold-light p-10" data-testid="tour-inquiry-success">
        <CheckCircle2 className="h-10 w-10 text-gold" strokeWidth={1.3} />
        <h3 className="mt-5 font-serif text-3xl text-ink">Thank you. Your day on Rhodes is in good hands.</h3>
        <p className="mt-3 text-slate-600">We will reply shortly with a tailored plan and price. Check your inbox for a copy of your request.</p>
      </div>
    );

  return (
    <form onSubmit={submit} noValidate className="card-soft grid gap-5 border border-slate-200 bg-white p-6 shadow-[0_30px_60px_-40px_rgba(0,0,0,0.25)] sm:grid-cols-2 md:p-10" data-testid="tour-inquiry-form">
      <input className="hp-field" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} aria-hidden="true" name="website" />
      <div className="sm:col-span-2">
        <span className="field-label">Tour</span>
        <TourTypeToggle value={f.tour_type} onChange={set("tour_type")} />
      </div>
      <Field label="Full name" id="tour-name" error={errors.name}>
        <input id="tour-name" className="field-input" value={f.name} onChange={set("name")} data-testid="tour-name-input" />
      </Field>
      <Field label="Email" id="tour-email" error={errors.email}>
        <input id="tour-email" type="email" className="field-input" value={f.email} onChange={set("email")} data-testid="tour-email-input" />
      </Field>
      <Field label="WhatsApp / phone" id="tour-phone" error={errors.phone}>
        <div className="flex gap-2">
          <select className="field-input !w-28 shrink-0" value={f.phone_code} onChange={set("phone_code")} data-testid="tour-phone-code-select" aria-label="Country code">
            {CODES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input id="tour-phone" type="tel" className="field-input" placeholder="Mobile number" value={f.phone} onChange={set("phone")} data-testid="tour-phone-input" />
        </div>
      </Field>
      <Field label="Preferred date" id="tour-date">
        <input id="tour-date" type="date" className="field-input" value={f.date} onChange={set("date")} data-testid="tour-date-input" />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Pickup location" id="tour-pickup" error={errors.pickup_location}>
          <input id="tour-pickup" className="field-input" placeholder="Hotel name or address" value={f.pickup_location} onChange={set("pickup_location")} data-testid="tour-pickup-location-input" />
        </Field>
      </div>
      <Field label="Pickup time" id="tour-pickup-time" error={errors.pickup_time}>
        <input id="tour-pickup-time" type="time" className="field-input" value={f.pickup_time} onChange={set("pickup_time")} data-testid="tour-pickup-time-input" />
      </Field>
      <Field label="Drop-off time" id="tour-dropoff-time">
        <input id="tour-dropoff-time" type="time" className="field-input" value={f.dropoff_time} onChange={set("dropoff_time")} data-testid="tour-dropoff-time-input" />
      </Field>
      <Field label="Number of guests" id="tour-guests" error={errors.guests}>
        <input id="tour-guests" type="number" min={1} max={50} className="field-input" value={f.guests} onChange={set("guests")} data-testid="tour-guests-input" />
      </Field>
      <Field label="Child seats (free)" id="tour-child-seats">
        <select id="tour-child-seats" className="field-input" value={f.child_seat_count} onChange={set("child_seat_count")} data-testid="tour-child-seats-select">
          <option value={0}>No child seats</option>
          {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} child seat{n > 1 ? "s" : ""}</option>)}
        </select>
      </Field>
      <div className="sm:col-span-2">
        <span className="field-label">Interests</span>
        <div className="flex flex-wrap gap-2">
          {TOUR_INTERESTS.map((i) => (
            <button
              type="button"
              key={i}
              onClick={() => toggleInterest(i)}
              data-testid={`tour-interest-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`rounded-full border px-4 py-2 text-sm transition-colors ${f.interests.includes(i) ? "border-gold bg-gold-light text-ink" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
            >
              {i}
            </button>
          ))}
        </div>
      </div>
      <div className="sm:col-span-2">
        <Field label="Message" id="tour-message">
          <textarea id="tour-message" rows={4} className="field-input" placeholder="Tell us what you would love to see and do." value={f.message} onChange={set("message")} data-testid="tour-message-input" />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <button type="submit" disabled={sending} className="btn btn-gold w-full sm:w-auto" data-testid="tour-inquiry-submit-button">
          {sending ? "Sending…" : "Send my tour request"}
        </button>
      </div>
    </form>
  );
};
